<template>
  <div class="category-color-bar">
    <div
      v-for="(step, index) in steps"
      :key="step.value"
      :class="{
        'is-active': activeStep === index,
        'is-checked': isStepChecked(step),
      }"
      :style="`--stepColor: #${step.color}`"
      class="category-color-bar__color"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { IStepItem } from "@/modules/self-assessment/types/step-item.interface";
import { ICategory } from "@/services/data/category/category.interface";
import { ICategoryLevelSelection } from "@/services/data/viral-level/viral-level.interface";
import { IViralLevelState } from "@/services/store/viral-level/viral-level-types";
import { ILevel } from "@/services/data/level/level.interface";

export default defineComponent({
  name: "CategoryColorBar",

  props: {
    /**
     * Allows to mark a step as active.
     */
    activeStep: {
      type: Number,
      default: -1,
    },
  },

  computed: {
    categories(): Array<ICategory> {
      return this.$store.state.categories.data;
    },

    steps(): Array<IStepItem> {
      return this.categories.map((item: ICategory) => ({
        value: item.id,
        label: item.name.replace(" and ", " & "),
        color: item.color,
      })) as Array<IStepItem>;
    },

    /**
     * Available levels.
     */
    levels(): Array<ICategoryLevelSelection> {
      const viralLevelState = this.$store.state.viralLevel as IViralLevelState;
      return viralLevelState.levels;
    },

    /**
     * Retrieves an array with all checked categories.
     */
    checkedSteps(): Array<number> {
      return this.levels
        .filter(
          (record: ICategoryLevelSelection) =>
            (record.level as ILevel["value"]) > 0,
        )
        .map((record: ICategoryLevelSelection) => record.category);
    },
  },

  methods: {
    /**
     * Checks if the given step is part of the set of
     * current selected steps.
     */
    isStepChecked(step: IStepItem): boolean {
      return this.checkedSteps.includes(step.value);
    },
  },
});
</script>

<style lang="scss" scoped>
.category-color-bar {
  display: flex;
  justify-content: center;
  min-width: 100%;
}

.category-color-bar__color {
  flex-grow: 1;
  width: auto;
  height: 12px;
  margin: 0 1px;

  background-color: $athens-gray;

  &.is-checked,
  &.is-active {
    background-color: var(--stepColor);
  }
}
</style>
